import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { captureException, getCurrentScope, init, setTag, setUser } from '@sentry/angular';
import { PICFLOW_CONFIG } from '../picflow-config';
import { BrowserService } from './browser.service';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private config = inject(PICFLOW_CONFIG);
  private browserService = inject(BrowserService);
  private platformId = inject(PLATFORM_ID);

  constructor() {
    if (this.browserService.isExtension()) return;
    init({
      dsn: 'https://f2ba0c9cbcc347ff992faf283bb386b6@o1075862.ingest.sentry.io/6076852',
      integrations: [],
      release: this.config.version,
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'gsi/client',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Random errors that we know are safe
        'Unknown Error',
        'ChunkLoadError',
        'Blocked a frame with origin',
        '@webkit-masked-url(://hidden/)',
        'Minified invariant',
        'clearTransInfo',
        'needMaskingText',
        'styleSheetObserver',
        'clarity.js',
        'Ar.version',
        'googleads.g.doubleclick.net',
        '/gsi/client',
        '/clarity',
        'Lr.version',
        'this.elContainer.parentElement.insertBefore', // pinterest
        'ResizeObserver loop completed with',
        'The operation was aborted',
        '_sm_nck',
        'viewthroughconversion',
        'Fr.version',
        "reading 'version')",
        'Unable to auto-detect a suitable renderer.',
        'NoDecodeDelegateForThisImageFormat',
        'type=error',
        '_AutofillCallbackHandler',
        'Importing a module script failed',
        'Data cannot be cloned, out of memory',
        'ClerkJS: Token refresh failed',
        'NG01002', // Expression Changed After Checked
        'sendObjectBeacon',
        'This operation was aborted',
        'IFrameMessageHandler',
        'disableWebkitEnterFullscreen',
        'TypeError: Failed to fetch',
        'called during event replay',
        'ClerkJS components are not ready yet',
        'Cannot convert 22 to a BigInt',
        'convert 22 to BigInt',
        `(setting '__ngContext__')`,
        `Unexpected token 'else'`,
        'NG0953',
        'NG0750',
        'logMutedMessage', // https://github.com/getsentry/sentry-javascript/issues/8444
        'AbortError',
        'type=unhandledrejection) captured as promise rejection',
        'e._tag',
        'this.shouldEnforce',
        'is not valid JSON',
      ],
      denyUrls: [
        'googletagmanager',
        'accounts.google.com',
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        'https://snid.snitcher.com',
        '/analytics-dot-js-integrations/',
        '/next-integrations/',
        '_sm_nck',
        'clarity.js',
        'intercomcdn.com',
        'beacon.min.js',
      ],
      // tracesSampler: samplingContext => {
      //   const pageName = samplingContext.transactionContext.name || '';
      //   if (pageName === '/') return 0.01;
      //   if (pageName === '/auth/authorize') return 0.01;
      //   if (pageName.startsWith('/access')) return 0.01;
      //   if (pageName.includes('/review/')) return 0.01;
      //   return 0.02;
      // },
    });

    const currentScope = getCurrentScope();
    currentScope.addEventProcessor(event => {
      event.environment = this.config.env;
      return event;
    });
    setTag('is_ssr', isPlatformServer(this.platformId));
  }

  captureException(message: string | Error | HttpErrorResponse, extra = {}) {
    if (message && typeof message !== 'string') {
      if ('code' in message) {
        message = `${message.message} - ${message.code}`;
      }
    }
    captureException(message, { extra });
  }

  setUser(user: { [key: string]: unknown }) {
    setUser(user);
  }
}
